import type {TraceIconKey} from './dummySupervisorData';

export interface LiveTraceStep {
  key: string;
  icon: TraceIconKey;
  label: string;
  sub?: string;
  status: 'running' | 'done' | 'error';
  resultMeta?: string;
  // Undefined renders as a top-level step (not nested), matching the
  // mockup's top-level Supervisor/Insights steps vs. nested tool-call groups.
  group?: string;
}

export interface LiveTraceState {
  steps: LiveTraceStep[];
  sources: string[];
}

export const createLiveTraceState = (): LiveTraceState => ({steps: [], sources: []});

// Planner-side events (run_*, plan_written, node_*, final_result) wrap their
// structured fields in a nested `data` object alongside a human-readable
// top-level `message`; node_started additionally duplicates node_id/node_name
// at the top level. QUO's own events (queued, step_start/done,
// pipeline_complete) are flat. This reads either shape.
const field = (payload: any, key: string): any => payload?.data?.[key] ?? payload?.[key];

const QUO_STEP_META: Record<string, {icon: TraceIconKey; label: string}> = {
  normalize: {icon: 'route', label: 'Understanding your question'},
  ground: {icon: 'graph', label: 'Grounding entities in the knowledge graph'},
  decompose: {icon: 'code', label: 'Breaking down your question'},
  intent: {icon: 'shield', label: 'Classifying intent'},
  capability: {icon: 'db', label: 'Selecting the right tools'},
  dag: {icon: 'whatif', label: 'Building an execution plan'},
};

export const iconForCapability = (capabilityId?: string): TraceIconKey => {
  const id = (capabilityId || '').toLowerCase();
  if (id.includes('forecast')) return 'forecast';
  if (id.includes('whatif') || id.includes('scenario')) return 'whatif';
  if (id.includes('rca') || id.includes('root_cause')) return 'rca';
  if (id.includes('graph') || id.includes('kg')) return 'graph';
  return 'db';
};

const truncate = (text: unknown, max = 160): string | undefined => {
  if (typeof text !== 'string' || !text) return undefined;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const upsertStep = (
  state: LiveTraceState,
  key: string,
  patch: Partial<Omit<LiveTraceStep, 'key'>>,
): LiveTraceState => {
  const idx = state.steps.findIndex((s) => s.key === key);
  const steps = [...state.steps];
  if (idx === -1) {
    steps.push({key, icon: 'spark', label: key, status: 'running', ...patch});
  } else {
    steps[idx] = {...steps[idx], ...patch};
  }
  return {...state, steps};
};

// Reduces one parsed SSE event from the /api/v2/ask/stream QUO+planner
// stream into the next live-trace state, for the <LiveAgentTrace /> UI.
export const applySupervisorStreamEvent = (
  state: LiveTraceState,
  eventName: string,
  payload: any,
): LiveTraceState => {
  switch (eventName) {
    case 'queued':
      return upsertStep(state, 'quo:queued', {
        icon: 'route',
        label: payload?.message || 'Understanding your question…',
        status: 'running',
        group: 'Query Classification',
      });

    case 'step_start': {
      const meta = QUO_STEP_META[payload?.step] || {icon: 'route' as TraceIconKey, label: payload?.step};
      return upsertStep(state, `quo:${payload?.step}`, {
        icon: meta.icon,
        label: payload?.message || meta.label,
        status: 'running',
        group: 'Query Classification',
      });
    }

    case 'step_done': {
      const meta = QUO_STEP_META[payload?.step] || {icon: 'route' as TraceIconKey, label: payload?.step};
      return upsertStep(state, `quo:${payload?.step}`, {
        icon: meta.icon,
        // Prefer the event's own message over our hardcoded label when present.
        label: payload?.message || meta.label,
        status: 'done',
        resultMeta: typeof payload?.secs === 'number' ? `${payload.secs.toFixed(1)}s` : 'done',
        group: 'Query Classification',
      });
    }

    case 'pipeline_complete': {
      const subQuery = payload?.quo?.sub_queries?.[0];
      const sourceNames: string[] = [
        ...(subQuery?.entities || []).map((e: any) => e?.name),
        ...(subQuery?.modules || []).map((m: any) => m?.name),
        ...(subQuery?.domains || []).map((d: any) => d?.name),
      ].filter(Boolean);
      const next = upsertStep(state, 'quo:queued', {status: 'done', resultMeta: 'query understood'});
      return sourceNames.length > 0
        ? {...next, sources: Array.from(new Set(sourceNames))}
        : next;
    }

    // case 'run_accepted':
    // case 'run_started':
    //   return upsertStep(state, 'plan:run', {
    //     icon: 'route',
    //     label: 'Planner — preparing analysis',
    //     status: 'running',
    //     group: 'Planning',
    //   });

    // case 'plan_written':
    //   return upsertStep(state, 'plan:run', {
    //     icon: 'route',
    //     label: 'Planner — preparing analysis',
    //     status: 'done',
    //     resultMeta: `plan written · ${field(payload, 'nodes') ?? '?'} task(s), ${field(payload, 'waves') ?? '?'} wave(s)`,
    //     group: 'Planning',
    //   });

    case 'node_started': {
      const key = `node:${field(payload, 'node_id')}`;
      const newLabel = field(payload, 'node_name') || field(payload, 'capability_id') || 'Agent';
      const existing = state.steps.find((s) => s.key === key);

      // A node_id can legitimately fire node_started more than once (e.g.
      // retried with a fallback capability/agent after node_primary_failed)
      // — join those attempts onto the same row instead of a duplicate step.
      if (existing) {
        // Ignore a stray/duplicate node_started once the node has already
        // finished, so it doesn't flip a done/error row back to running.
        if (existing.status !== 'running') return state;
        return upsertStep(state, key, {
          label: existing.label && existing.label !== newLabel ? `${existing.label} → ${newLabel}` : existing.label,
          sub: field(payload, 'sub_query') || existing.sub,
          status: 'running',
          group: 'Execution',
        });
      }

      return upsertStep(state, key, {
        icon: iconForCapability(field(payload, 'capability_id')),
        label: newLabel,
        sub: field(payload, 'sub_query'),
        status: 'running',
        group: 'Execution',
      });
    }

    case 'node_primary_failed':
      return upsertStep(state, `node:${field(payload, 'node_id')}`, {
        status: 'running',
        resultMeta: 'retrying with a different approach…',
        group: 'Execution',
      });

    case 'run_completed':
      return upsertStep(state, 'synthesis', {
        icon: 'spark',
        label: 'Synthesizing Insights',
        status: field(payload, 'outcome') === 'failure' ? 'error' : 'running',
      });

    case 'final_result': {
      const outcome = field(payload, 'result')?.outcome;
      return upsertStep(state, 'synthesis', {
        icon: 'spark',
        label: 'Synthesizing Insights',
        status: outcome === 'failure' ? 'error' : 'done',
        resultMeta: outcome === 'failure' ? 'completed with issues' : 'analysis ready',
      });
    }

    default:
      // Covers node_failed and any future node_<terminal-state> variants not
      // explicitly listed above.
      if (eventName.startsWith('node_')) {
        const isError = eventName.includes('failed') || eventName.includes('error');
        return upsertStep(state, `node:${field(payload, 'node_id')}`, {
          status: isError ? 'error' : 'done',
          resultMeta: isError ? truncate(field(payload, 'error_message')) || 'failed' : 'done',
          group: 'Execution',
        });
      }
      return state;
  }
};
